import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Datepicker, Select } from "flowbite-react";
import { useEmployeeManagement } from "../context/EmployeeManagementContextHelper";
import InputMask from "react-input-mask";

const AddEmployeePage = () => {
  const { createEmployee, companyProfile } = useEmployeeManagement();
  const navigate = useNavigate();

  const USStates = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ];

  const today = new Date();
  const maxDateForDateOfBirth = new Date(
    today.getFullYear() - 16,
    today.getMonth(),
    today.getDate()
  );

  const maxHireDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const minTerminationDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + 1
  );

  const formatDateToLocalISOString = (date) => {
    const offset = date.getTimezoneOffset();
    const adjustedDate = new Date(date.getTime() - offset * 60 * 1000);
    return adjustedDate.toISOString().split("T")[0];
  };

  const [firstName, setFirstName] = useState("");
  const [firstNameError, setFirstNameError] = useState("");

  const [middleName, setMiddleName] = useState("");
  const [middleNameError, setMiddleNameError] = useState("");

  const [lastName, setLastName] = useState("");
  const [lastNameError, setLastNameError] = useState("");

  const [dateOfBirth, setDateOfBirth] = useState(maxDateForDateOfBirth);
  const [hireDate, setHireDate] = useState(maxHireDate);
  const [terminationDate, setTerminationDate] = useState(minTerminationDate);
  const [vestingSchedule] = useState(companyProfile.vesting_schedule);

  const [ssn, setSsn] = useState("");
  const [ssnError, setSsnError] = useState("");

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");

  const [address, setAddress] = useState("");
  const [addressError, setAddressError] = useState("");

  const [city, setCity] = useState("");
  const [cityError, setCityError] = useState("");

  const [state, setState] = useState("");
  const [stateError, setStateError] = useState("");

  const [zipcode, setZipcode] = useState("");
  const [zipcodeError, setZipcodeError] = useState("");

  const [jobTitle, setJobTitle] = useState("");
  const [jobTitleError, setJobTitleError] = useState("");

  const [payRate, setPayRate] = useState("");
  const [payRateError, setPayRateError] = useState("");

  const [payPeriod, setPayPeriod] = useState("");
  const [payPeriodError, setPayPeriodError] = useState("");

  const [annualSalary, setAnnualSalary] = useState("");
  const [annualSalaryError, setAnnualSalaryError] = useState("");

  const [employmentStatus, setEmploymentStatus] = useState("");
  const [employmentStatusError, setEmploymentStatusError] = useState("");

  const validateFirstName = (newFirstName) => {
    if (!newFirstName.trim()) {
      setFirstNameError("First name is required");
      return false;
    }

    if (!/^[a-zA-Z]+$/.test(newFirstName)) {
      setFirstNameError("First name should only contain alphabetic characters");
      return false;
    }
    setFirstNameError("");
    return true;
  };

  const validateMiddleName = (newMiddleName) => {
    if (newMiddleName && !/^[a-zA-Z]+$/.test(newMiddleName)) {
      setMiddleNameError(
        "Middle name should only contain alphabetic characters"
      );
      return false;
    }
    return true;
  };

  const validateLastName = (newLastName) => {
    if (!newLastName.trim()) {
      setLastNameError("Last name is required");
      return false;
    }
    if (!/^[a-zA-Z]+$/.test(newLastName)) {
      setLastNameError("Last name should only contain alphabetic characters");
      return false;
    }
    setLastNameError("");
    return true;
  };

  const validateSsn = (newSsn) => {
    if (!newSsn.trim()) {
      setSsnError("Social Security Number is required");
      return false;
    }
    if (!/^\d{3}-\d{2}-\d{4}$/.test(newSsn)) {
      setSsnError(
        "Invalid SSN format. Format should be XXX-XX-XXXX with numbers only"
      );
      return false;
    }
    const parts = newSsn.split("-");
    if (parts[0] === "000" || parts[1] === "00" || parts[2] === "0000") {
      setSsnError("Invalid SSN. One of the series is invalid");
      return false;
    }
    if (parts[0] === "666" || (parts[0] >= "900" && parts[0] <= "999")) {
      setSsnError("Invalid SSN. The first series is invalid");
      return false;
    }

    setSsnError("");
    return true;
  };

  const validateEmail = (newEmail) => {
    if (!newEmail.trim()) {
      setEmailError("Email is required");
      return false;
    }
    if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(newEmail)) {
      setEmailError("Invalid email format. Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePhoneNumber = (newPhoneNumber) => {
    if (!newPhoneNumber) {
      setPhoneNumberError("Phone number is required");
      return false;
    }

    if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(newPhoneNumber)) {
      setPhoneNumberError('Invalid phone number format. Format should be (XXX) XXX-XXXX');
      return false;
    }

    setPhoneNumberError("");
    return true;
  };

  const validateAddress = (newAddress) => {
    if (!newAddress.trim()) {
      setAddressError("Address is required");
      return false;
    }
    setAddressError("");
    return true;
  };

  const validateCity = (newCity) => {
    if (!newCity.trim()) {
      setCityError("City is required");
      return false;
    }
    setCityError("");
    return true;
  };

  const validateState = (newState) => {
    if (!newState.trim()) {
      setStateError("State is required");
      return false;
    }
    setStateError("");
    return true;
  };

  const validateZipcode = (newZipcode) => {
    if (!newZipcode.trim()) {
      setZipcodeError("Zip Code is required");
      return false;
    }
    setZipcodeError("");
    return true;
  };

  const validateJobTitle = (newJobTitle) => {
    if (!newJobTitle.trim()) {
      setJobTitleError("Job title is required");
      return false;
    }
    setJobTitleError("");
    return true;
  };

  const validatePayRate = (newPayRate) => {
    if (!newPayRate.trim()) {
      setPayRateError("Pay rate is required");
      return false;
    }
    setPayRateError("");
    return true;
  };

  const validatePayPeriod = (newPayPeriod) => {
    if (!newPayPeriod.trim()) {
      setPayPeriodError("Choose pay period");
      return false;
    }
    setPayPeriodError("");
    return true;
  };

  const validateAnnualSalary = (newAnnualSalary) => {
    if (!newAnnualSalary.trim()) {
      setAnnualSalaryError("Annual salary is required");
      return false;
    }
    setAnnualSalaryError("");
    return true;
  };

  const validateEmployementStatus = (newEmployementStatus) => {
    if (!newEmployementStatus.trim()) {
      setEmploymentStatusError("Choose employement status");
      return false;
    }
    setEmploymentStatusError("");
    return true;
  };

  const handleFirstNameChange = (e) => {
    const newFirstName = e.target.value;
    setFirstName(newFirstName);
    if (firstNameError) validateFirstName(newFirstName);
  };

  const handleMiddleNameChange = (e) => {
    const newMiddleName = e.target.value;
    setMiddleName(newMiddleName);
    if (middleNameError) validateMiddleName(newMiddleName);
  };

  const handleLastNameChange = (e) => {
    const newLastName = e.target.value;
    setLastName(newLastName);
    if (lastNameError) validateLastName(newLastName);
  };

  const handleSsnChange = (e) => {
    const newSsn = e.target.value;
    setSsn(newSsn);
    if (ssnError) validateSsn(newSsn);
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (emailError) validateEmail(newEmail);
  };

  const handlePhoneNumberChange = (e) => {
    const newPhoneNumber = e.target.value;
    setPhoneNumber(newPhoneNumber);
    if (phoneNumberError) validatePhoneNumber(newPhoneNumber);
  };

  const handleAddressChange = (e) => {
    const newAddress = e.target.value;
    setAddress(newAddress);
    if (addressError) validateAddress(newAddress);
  };

  const handleCityChange = (e) => {
    const newCity = e.target.value;
    setCity(newCity);
    if (cityError) validateCity(newCity);
  };

  const handleStateChange = (e) => {
    const newState = e.target.value;
    setState(newState);
    if (stateError) validateState(newState);
  };

  const handleZipcodeChange = (e) => {
    const newZipcode = e.target.value;
    setZipcode(newZipcode);
    if (zipcodeError) validateZipcode(newZipcode);
  };

  const handleJobTitleChange = (e) => {
    const newJobTitle = e.target.value;
    setJobTitle(newJobTitle);
    if (jobTitleError) validateJobTitle(newJobTitle);
  };

  const handlePayRateChange = (e) => {
    const newPayRate = e.target.value;
    setPayRate(newPayRate);
    if (payRateError) validatePayRate(newPayRate);
  };

  const handlePayPeriodeChange = (e) => {
    const newPayPeriod = e.target.value;
    setPayPeriod(newPayPeriod);
    if (payPeriodError) validatePayPeriod(newPayPeriod);
  };

  const handleAnnualSalaryChange = (e) => {
    const newAnnualSalary = e.target.value;
    setAnnualSalary(newAnnualSalary);
    if (annualSalaryError) validateAnnualSalary(newAnnualSalary);
  };

  const handleEmployementStatusChange = (e) => {
    const newEmployementStatus = e.target.value;
    setEmploymentStatus(newEmployementStatus);
    if (employmentStatusError) validateEmployementStatus(newEmployementStatus);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const isFirstNameValid = validateFirstName(firstName);
    const isMiddleNameValid = validateMiddleName(middleName);
    const isLastNameValid = validateLastName(lastName);
    const isSsnValid = validateSsn(ssn);
    const isEmailValid = validateEmail(email);
    const isPhoneNumberValid = validatePhoneNumber(phoneNumber);
    const isAddressValid = validateAddress(address);
    const isCityValid = validateCity(city);
    const isStateValid = validateState(state);
    const isZipcodeValid = validateZipcode(zipcode);
    const isJobTitleValid = validateJobTitle(jobTitle);
    const isPayRateValid = validatePayRate(payPeriod);
    const isAnnualSalaryValid = validateAnnualSalary(annualSalary);
    const isPayPeriodValid = validatePayPeriod(payPeriod);
    const isEmployementStatusValid =
      validateEmployementStatus(employmentStatus);

    if (
      !isFirstNameValid ||
      !isMiddleNameValid ||
      !isLastNameValid ||
      !isSsnValid ||
      !isEmailValid ||
      !isPhoneNumberValid ||
      !isAddressValid ||
      !isCityValid ||
      !isStateValid ||
      !isZipcodeValid ||
      !isJobTitleValid ||
      !isPayRateValid ||
      !isAnnualSalaryValid ||
      !isPayPeriodValid ||
      !isEmployementStatusValid
    ) {
      return;
    }
    createEmployee(
      firstName,
      middleName,
      lastName,
      formatDateToLocalISOString(dateOfBirth),
      ssn,
      email,
      phoneNumber,
      address,
      city,
      state,
      zipcode,
      jobTitle,
      vestingSchedule,
      formatDateToLocalISOString(hireDate),
      payRate,
      payPeriod,
      annualSalary,
      employmentStatus,
      formatDateToLocalISOString(terminationDate)
    );
    navigate("/dashboard/employee-management");
  };

  return (
    <div className="flex flex-col gap-4 w-full xl:w-3/4">
      <div className="w-full py-4 border-b border-[#003464]">
        <h2 className="text-gray-900 text-xl md:text-2xl lg:text-3xl xl:text-5xl font-bold">
          Create Employee Profile
        </h2>
      </div>
      <div className="flex flex-col lg:flex-row justify-between gap-4">
        <div className="flex flex-col md:flex-row justify-between w-full gap-4">
          <div className="w-full">
            <div className="relative w-full">
              <input
                type="text"
                id="first_name"
                className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  ${
                  firstName.trim()
                    ? "border-green-600 focus:border-green-600"
                    : "focus:border-[#003464]"
                } ${
                  firstNameError && "border-red-600 focus:border-red-600"
                } peer`}
                placeholder=" "
                onChange={handleFirstNameChange}
              />
              <label
                htmlFor="first_name"
                className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
                  firstName.trim()
                    ? "text-green-600 peer-focus:text-green-600"
                    : "peer-focus:text-[#003464]"
                }
              ${firstNameError && "text-red-600 peer-focus:text-red-600"}
              `}
              >
                First Name
              </label>
            </div>
            {firstNameError && (
              <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
                <span className="font-medium">Oh, snapp! </span>
                {firstNameError}.
              </p>
            )}
          </div>
          <div className="md:w-1/2">
            <div className="relative md:w-full">
              <input
                type="text"
                id="middle_name"
                className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer ${
                  middleName.trim()
                    ? "border-green-600 focus:border-green-600"
                    : "focus:border-[#003464]"
                } ${middleNameError && "border-red-600 focus:border-red-600"}`}
                placeholder=" "
                onChange={handleMiddleNameChange}
              />
              <label
                htmlFor="middle_name"
                className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
                  middleName.trim()
                    ? "text-green-600 peer-focus:text-green-600"
                    : "peer-focus:text-[#003464]"
                } ${middleNameError && "text-red-600 peer-focus:text-red-600"}`}
              >
                Middle Name
              </label>
            </div>
            {middleNameError && (
              <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
                <span className="font-medium">Oh, snapp! </span>
                {middleNameError}.
              </p>
            )}
          </div>
        </div>
        <div className="w-full">
          <div className="relative w-full">
            <input
              type="text"
              id="last_name"
              className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer ${
                lastName.trim()
                  ? "border-green-600 focus:border-green-600"
                  : "focus:border-[#003464]"
              } ${lastNameError && "border-red-600 focus:border-red-600"}`}
              placeholder=" "
              onChange={handleLastNameChange}
            />
            <label
              htmlFor="last_name"
              className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
                lastName.trim()
                  ? "text-green-600 peer-focus:text-green-600"
                  : "peer-focus:text-[#003464]"
              }
            ${lastNameError && "text-red-600 peer-focus:text-red-600"}`}
            >
              Last Name
            </label>
          </div>
          {lastNameError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {lastNameError}.
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-col lg:flex-row w-full gap-4">
        <div className="relative w-full">
          <label
            htmlFor="date_of_birth"
            className="absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
          >
            Date Of Birth
          </label>
          <Datepicker
            onSelectedDateChanged={(e) => {
              setDateOfBirth(e);
            }}
            id="date_of_birth"
            title="Date Of Birth"
            maxDate={maxDateForDateOfBirth}
            defaultDate={maxDateForDateOfBirth}
            showClearButton={false}
            showTodayButton={false}
            theme={{
              root: {
                base: "relative w-full",
                input: {
                  field: {
                    base: "relative w-full",
                    input: {
                      base: "block w-full border border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 bg-white text-sm pl-10 rounded-lg focus:outline-none focus:ring-0 focus:border-[#003464]",
                      colors: {
                        gray: "bg-white",
                      },
                      sizes: {
                        md: "px-2.5 pb-3.5 pt-3",
                      },
                    },
                  },
                },
              },
              views: {
                days: {
                  items: {
                    item: {
                      selected: "bg-[#003464] text-white hover:bg-[#c96b1a]",
                    },
                  },
                },
                months: {
                  items: {
                    item: {
                      selected: "bg-[#003464] text-white hover:bg-[#c96b1a]",
                    },
                  },
                },
                decades: {
                  items: {
                    item: {
                      selected: "bg-[#003464] text-white hover:bg-[#c96b1a]",
                    },
                  },
                },
              },
            }}
          />
        </div>
        <div className="w-full">
          <div className="relative w-full">
            <InputMask
              mask={"999-99-9999"}
              type="text"
              id="social_security_number"
              className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer ${
                ssn.trim() && ssn !== "___-__-____"
                  ? "border-green-600 focus:border-green-600"
                  : "focus:border-[#003464]"
              } ${ssnError && "border-red-600 focus:border-red-600"}`}
              placeholder=" "
              onChange={handleSsnChange}
            />
            <label
              htmlFor="social_security_number"
              className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
                ssn.trim() && ssn !== "___-__-____"
                  ? "text-green-600 peer-focus:text-green-600"
                  : "peer-focus:text-[#003464]"
              }
            ${ssnError && "text-red-600 peer-focus:text-red-600"}`}
            >
              Social Security Number (<span>SSN</span>)
            </label>
          </div>
          {ssnError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {ssnError}.
            </p>
          )}
        </div>
        <div className="w-full">
          <div className="relative w-full">
            <input
              onChange={handleEmailChange}
              type="email"
              id="email"
              className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer ${
                email.trim()
                  ? "border-green-600 focus:border-green-600"
                  : "focus:border-[#003464]"
              } ${emailError && "border-red-600 focus:border-red-600"}`}
              placeholder=" "
            />
            <label
              htmlFor="email"
              className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
                email.trim()
                  ? "text-green-600 peer-focus:text-green-600"
                  : "peer-focus:text-[#003464]"
              }
            ${emailError && "text-red-600 peer-focus:text-red-600"}`}
            >
              Email
            </label>
          </div>
          {emailError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {emailError}.
            </p>
          )}
        </div>
        <div className="w-full">
          <div className="relative w-full">
            <InputMask
              mask="(999) 999-9999"
              type="text"
              id="phone_number"
              className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer ${
                phoneNumber && phoneNumber.trim()
                  ? "border-green-600 focus:border-green-600"
                  : "focus:border-[#003464]"
              } ${phoneNumberError && "border-red-600 focus:border-red-600"}`}
              placeholder=" "
              onChange={handlePhoneNumberChange}
              value={phoneNumber}
            />
            <label
              htmlFor="phone_number"
              className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
                phoneNumber && phoneNumber.trim()
                  ? "text-green-600 peer-focus:text-green-600"
                  : "peer-focus:text-[#003464]"
              }
              ${phoneNumberError && "text-red-600 peer-focus:text-red-600"}`}
            >
              Phone Number
            </label>
          </div>
          {phoneNumberError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {phoneNumberError}.
            </p>
          )}
        </div>
      </div>
      <div className="w-full">
        <div className="relative w-full">
          <input
            type="text"
            id="address"
            className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer ${
              address.trim()
                ? "border-green-600 focus:border-green-600"
                : "focus:border-[#003464]"
            } ${addressError && "border-red-600 focus:border-red-600"}`}
            placeholder=" "
            onChange={handleAddressChange}
          />
          <label
            htmlFor="address"
            className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
              address.trim()
                ? "text-green-600 peer-focus:text-green-600"
                : "peer-focus:text-[#003464]"
            }
        ${addressError && "text-red-600 peer-focus:text-red-600"}`}
          >
            Address
          </label>
        </div>
        {addressError && (
          <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
            <span className="font-medium">Oh, snapp! </span>
            {addressError}.
          </p>
        )}
      </div>
      <div className="flex flex-col md:flex-row w-full gap-4">
        <div className="w-full">
          <div className="relative w-full">
            <input
              type="text"
              id="city"
              className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer ${
                city.trim()
                  ? "border-green-600 focus:border-green-600"
                  : "focus:border-[#003464]"
              } ${cityError && "border-red-600 focus:border-red-600"}`}
              placeholder=" "
              onChange={handleCityChange}
            />
            <label
              htmlFor="city"
              className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
                city.trim()
                  ? "text-green-600 peer-focus:text-green-600"
                  : "peer-focus:text-[#003464]"
              }
          ${cityError && "text-red-600 peer-focus:text-red-600"}`}
            >
              City
            </label>
          </div>
          {cityError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {cityError}.
            </p>
          )}
        </div>
        <div className="w-full">
          <div className="relative w-full">
            <Select
              id="states"
              required
              className="w-full"
              onChange={handleStateChange}
              value={state}
              theme={{
                field: {
                  select: {
                    base: `block w-full border-1 border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 bg-white p-3 text-sm rounded-lg focus:outline-none focus:ring-0 focus:border-[#003464] ${
                      state
                        ? "border-green-600 focus:border-green-600"
                        : "focus:border-[#003464]"
                    } ${stateError && "border-red-600 focus:border-red-600"}`,
                    colors: {
                      gray: `bg-white ${!state && "text-gray-500"} ${
                        stateError &&
                        !state &&
                        "text-red-600 peer-focus:text-red-600"
                      }`,
                    },
                    sizes: {
                      md: "px-2.5 pb-3.5 pt-3",
                    },
                  },
                },
              }}
            >
              <option value={""}>Choose State</option>
              {USStates.map((usState, index) => {
                return <option key={index} value={usState}>{usState}</option>;
              })}
            </Select>
          </div>
          {stateError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {stateError}.
            </p>
          )}
        </div>
        <div className="w-full">
          <div className="relative w-full">
            <input
              type="text"
              id="zip_code"
              className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer ${
                zipcode.trim()
                  ? "border-green-600 focus:border-green-600"
                  : "focus:border-[#003464]"
              } ${zipcodeError && "border-red-600 focus:border-red-600"}`}
              placeholder=" "
              onChange={handleZipcodeChange}
            />
            <label
              htmlFor="zip_code"
              className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
                zipcode.trim()
                  ? "text-green-600 peer-focus:text-green-600"
                  : "peer-focus:text-[#003464]"
              }
          ${zipcodeError && "text-red-600 peer-focus:text-red-600"}`}
            >
              Zip Code
            </label>
          </div>
          {zipcodeError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {zipcodeError}.
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-col md:flex-row w-full gap-4">
        <div className="w-full">
          <div className="relative w-full">
            <input
              type="text"
              id="job_title"
              className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer ${
                jobTitle.trim()
                  ? "border-green-600 focus:border-green-600"
                  : "focus:border-[#003464]"
              } ${jobTitleError && "border-red-600 focus:border-red-600"}`}
              placeholder=" "
              onChange={handleJobTitleChange}
            />
            <label
              htmlFor="job_title"
              className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
                jobTitle.trim()
                  ? "text-green-600 peer-focus:text-green-600"
                  : "peer-focus:text-[#003464]"
              }
          ${jobTitleError && "text-red-600 peer-focus:text-red-600"}`}
            >
              Job Title
            </label>
          </div>
          {jobTitleError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {jobTitleError}.
            </p>
          )}
        </div>
        <div className="w-full">
          <div className="relative w-full">
            <input
              type="text"
              id="vesting_schedule"
              className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-green-600 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer"
              placeholder=" "
              disabled
              value={vestingSchedule}
            />
            <label
              htmlFor="vesting_schedule"
              className="absolute text-sm text-green-600 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
            >
              Vesting Schedule
            </label>
          </div>
        </div>

        <div className="relative w-full">
          <label
            htmlFor="hire_date"
            className="absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
          >
            Hire Date
          </label>
          <Datepicker
            id="hire_date"
            title="Hire Date"
            maxDate={maxHireDate}
            showClearButton={false}
            showTodayButton={false}
            onSelectedDateChanged={(e) => {
              setHireDate(e);
            }}
            theme={{
              root: {
                base: "relative w-full",
                input: {
                  field: {
                    base: "relative w-full",
                    input: {
                      base: "block w-full border border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 bg-white text-sm pl-10 rounded-lg focus:outline-none focus:ring-0 focus:border-[#003464]",
                      colors: {
                        gray: "bg-white",
                      },
                      sizes: {
                        md: "p-3",
                      },
                    },
                  },
                },
              },
              views: {
                days: {
                  items: {
                    item: {
                      selected: "bg-[#003464] text-white hover:bg-[#c96b1a]",
                    },
                  },
                },
                months: {
                  items: {
                    item: {
                      selected: "bg-[#003464] text-white hover:bg-[#c96b1a]",
                    },
                  },
                },
                decades: {
                  items: {
                    item: {
                      selected: "bg-[#003464] text-white hover:bg-[#c96b1a]",
                    },
                  },
                },
              },
            }}
          />
        </div>
      </div>
      <div className="flex flex-col md:flex-row w-full gap-4">
        <div className="w-full">
          <div className="relative w-full">
            <input
              type="text"
              id="pay_rate"
              className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer ${
                payRate.trim()
                  ? "border-green-600 focus:border-green-600"
                  : "focus:border-[#003464]"
              } ${payRateError && "border-red-600 focus:border-red-600"}`}
              placeholder=" "
              onChange={handlePayRateChange}
            />
            <label
              htmlFor="pay_rate"
              className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
                payRate.trim()
                  ? "text-green-600 peer-focus:text-green-600"
                  : "peer-focus:text-[#003464]"
              }
          ${payRateError && "text-red-600 peer-focus:text-red-600"}`}
            >
              Pay Rate
            </label>
          </div>
          {payRateError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {payRateError}.
            </p>
          )}
        </div>
        <div className="w-full">
          <div className="relative w-full">
            <Select
              id="pay_period"
              required
              className="w-full"
              onChange={handlePayPeriodeChange}
              theme={{
                field: {
                  select: {
                    base: `block w-full border-1 border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 bg-white p-3 text-sm rounded-lg focus:outline-none focus:ring-0 focus:border-[#003464] ${
                      payPeriod
                        ? "border-green-600 focus:border-green-600"
                        : "focus:border-[#003464]"
                    } ${
                      payPeriodError && "border-red-600 focus:border-red-600"
                    }`,
                    colors: {
                      gray: `bg-white ${!payPeriod && "text-gray-500"} ${
                        payPeriodError &&
                        !payPeriod &&
                        "text-red-600 peer-focus:text-red-600"
                      }`,
                    },
                    sizes: {
                      md: "px-2.5 pb-3.5 pt-3",
                    },
                  },
                },
              }}
            >
              <option value={""}>Choose Pay Period</option>
              <option value={"monthly"}>Monthly</option>
              <option value={"semi-monthly"}>Semi-monthly</option>
              <option value={"biweekly"}>Biweekly</option>
              <option value={"weekly"}>Weekly</option>
            </Select>
          </div>
          {payPeriodError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {payPeriodError}.
            </p>
          )}
        </div>
        <div className="w-full">
          <div className="relative w-full">
            <input
              type="text"
              id="annual_salary"
              className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-[#003464] peer ${
                annualSalary.trim()
                  ? "border-green-600 focus:border-green-600"
                  : "focus:border-[#003464]"
              } ${annualSalaryError && "border-red-600 focus:border-red-600"}`}
              placeholder=" "
              onChange={handleAnnualSalaryChange}
            />
            <label
              htmlFor="annual_salary"
              className={`absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 ${
                annualSalary.trim()
                  ? "text-green-600 peer-focus:text-green-600"
                  : "peer-focus:text-[#003464]"
              }
          ${annualSalaryError && "text-red-600 peer-focus:text-red-600"}`}
            >
              Annual Salary
            </label>
          </div>
          {annualSalaryError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {annualSalaryError}.
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-col md:flex-row w-full gap-4">
        <div className="w-full">
          <Select
            id="employement_status"
            required
            className="w-full"
            onChange={handleEmployementStatusChange}
            theme={{
              field: {
                select: {
                  base: `block w-full border-1 border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 bg-white p-2.5 text-sm rounded-lg focus:outline-none focus:ring-0 focus:border-[#003464] ${
                    employmentStatus
                      ? "border-green-600 focus:border-green-600"
                      : "focus:border-[#003464]"
                  } ${
                    employmentStatusError &&
                    "border-red-600 focus:border-red-600"
                  }`,
                  colors: {
                    gray: `bg-white ${!employmentStatus && "text-gray-500"} ${
                      employmentStatusError &&
                      !employmentStatus &&
                      "text-red-600 peer-focus:text-red-600"
                    }`,
                  },
                  sizes: {
                    md: "px-2.5 pb-3.5 pt-3",
                  },
                },
              },
            }}
          >
            <option value="">Choose Employement Status</option>
            <option value={"active"}>Active</option>
            <option value={"inactive"}>Inactive</option>
            <option value={"leave"}>Leave</option>
          </Select>
          {employmentStatusError && (
            <p className="ml-2 mt-2 text-xs text-red-600 dark:text-red-400 tracking-tighter">
              <span className="font-medium">Oh, snapp! </span>
              {employmentStatusError}.
            </p>
          )}
        </div>
        <div className="relative w-full">
          <label
            htmlFor="termination_date"
            className="absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-[#003464] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
          >
            Termination Date
          </label>
          <Datepicker
            id="termination_date"
            title="Termination Date"
            minDate={minTerminationDate}
            defaultDate={minTerminationDate}
            showClearButton={false}
            showTodayButton={false}
            onSelectedDateChanged={(e) => {
              setTerminationDate(e);
            }}
            theme={{
              root: {
                base: "relative w-full",
                input: {
                  field: {
                    base: "relative w-full",
                    input: {
                      base: "block w-full border border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 bg-white text-sm pl-10 rounded-lg focus:outline-none focus:ring-0 focus:border-[#003464]",
                      colors: {
                        gray: "bg-white",
                      },
                      sizes: {
                        md: "px-2.5 pb-3.5 pt-3",
                      },
                    },
                  },
                },
              },
              views: {
                days: {
                  items: {
                    item: {
                      selected: "bg-[#003464] text-white hover:bg-[#c96b1a]",
                    },
                  },
                },
                months: {
                  items: {
                    item: {
                      selected: "bg-[#003464] text-white hover:bg-[#c96b1a]",
                    },
                  },
                },
                decades: {
                  items: {
                    item: {
                      selected: "bg-[#003464] text-white hover:bg-[#c96b1a]",
                    },
                  },
                },
              },
            }}
          />
        </div>
      </div>
      <div className="flex flex-col md:flex-row w-full gap-4">
        <button
          className="p-0.5 rounded-md bg-[#003464] text-center text-sm font-semibold text-white shadow-sm hover:bg-[#c96b1a] flex flex-row justify-between items-center hover:shadow-lg hover:ring-2 hover:ring-[#c96b1a]"
          onClick={handleSubmit}
        >
          <span className="items-center transition-all duration-200 rounded-md text-sm px-4 py-2">
            Create
          </span>
        </button>
        <button
          className="p-0.5 rounded-md bg-white text-center text-sm font-semibold text-black shadow-sm hover:text-[#c96b1a] flex flex-row justify-between items-center hover:shadow-lg hover:ring-2 hover:ring-[#c96b1a] border border-gray-300"
          onClick={() => {
            navigate("/dashboard/employee-management");
          }}
        >
          <span className="transition-all duration-200 rounded-md text-sm px-4 py-2">
            Cancel
          </span>
        </button>
      </div>
    </div>
  );
};

export default AddEmployeePage;
